// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { createEntityAdapter, type EntityState } from '@reduxjs/toolkit';
import {
  type Advisor,
  type AdvisoryInstitution,
  type CreateAdvisoryAccountsCitecRequestBody,
  type Client,
} from '@shared/data-access/types/brokerage-types';
import { isNuvaraInversisInstitution } from '@shared/data-access/utils';
import { isClientApp } from '@shared/environment';

export interface RootClientsState {
  readonly ['clients']: ClientsState;
}

export type SelectedClient = Pick<Client, 'id' | 'name' | 'email'> &
  Partial<Pick<Client, 'citec_user_id' | 'risk_profile' | 'status'>>;

export type InstitutionWithClientId = AdvisoryInstitution & {
  client_id: number;
};

export interface ClientsState {
  readonly client: SelectedClient | null;
  readonly institutions: EntityState<InstitutionWithClientId, string>;
  readonly pendingInstitutions: EntityState<AdvisoryInstitution, string>;
  readonly advisors: EntityState<Advisor, number>;
  readonly bankUploadDraft: CreateAdvisoryAccountsCitecRequestBody | null;
}

export const institutionsAdapter = createEntityAdapter<InstitutionWithClientId>({
  sortComparer: (current, next) => current.name.localeCompare(next.name),
});

export const pendingInstitutionsAdapter = createEntityAdapter<AdvisoryInstitution>({
  sortComparer: (current, next) =>
    isNuvaraInversisInstitution(current)
      ? 1
      : current.name.localeCompare(next.name),
});

export const advisorsAdapter = createEntityAdapter<Advisor>({
  sortComparer: (current, next) =>
    (current.name || current.email).localeCompare(next.name || next.email),
});

export const initialState: ClientsState = isClientApp
  ? {
      client: { id: 0, name: '', email: '' },
      institutions: institutionsAdapter.getInitialState(),
      pendingInstitutions: pendingInstitutionsAdapter.getInitialState(),
      advisors: advisorsAdapter.getInitialState(),
      bankUploadDraft: null,
    }
  : {
      client: null,
      institutions: institutionsAdapter.getInitialState(),
      pendingInstitutions: pendingInstitutionsAdapter.getInitialState(),
      advisors: advisorsAdapter.getInitialState(),
      bankUploadDraft: null,
    };
